import once from 'lodash/once';

/**
 * Pop-up container for all purposes
 *
 * @type {Object}
 * @author HL
 */

const elevatedContainer = {
  containers: document.querySelectorAll('.elevated-container'),
  forceHide: false,
  lastFocusedElement: null,
  /**
   * Init engine. Wrapped in lodash once to make sure it's not called repeatedly
   */
  init: once(function() {
    elevatedContainer.containers = document.querySelectorAll('.elevated-container');

    elevatedContainer.containers.forEach(container => {
      let timer = container.getAttribute('data-timer') ? container.getAttribute('data-timer') : 0;

      let position = container.getAttribute('data-position') ? container.getAttribute('data-position') : false;
      let positionTop = container.getAttribute('data-placement-top') ? container.getAttribute('data-placement-top') : false;
      let positionRight = container.getAttribute('data-placement-right') ? container.getAttribute('data-placement-right') : false;
      let positionBottom = container.getAttribute('data-placement-bottom') ? container.getAttribute('data-placement-bottom') : false;
      let positionLeft = container.getAttribute('data-placement-left') ? container.getAttribute('data-placement-left') : false;
      let width = container.getAttribute('data-placement-width') ? container.getAttribute('data-placement-width') : false;
      let closeOnoverlayClicked = container.getAttribute('data-close-overlay-clicked')
        ? container.getAttribute('data-close-overlay-clicked')
        : false;

      if (closeOnoverlayClicked) {
        container.addEventListener('click', event => {
          if (event.target.classList.contains('elevated-container')) {
            this.toggleVisibility(container, false);
          }
        });
      }

      elevatedContainer.forceHide = false;
      elevatedContainer.validateContent(container);
      if (!elevatedContainer.forceHide) {
        elevatedContainer.getCustomAttrs(container);
        elevatedContainer.initToggleElements(container);

        if (!container.classList.contains('elevated-overlay')) {
          elevatedContainer.setPosition(position, positionTop, positionRight, positionBottom, positionLeft, width, container);
        }

        if (timer && container.getAttribute('data-show-on-open') !== 'never') {
          elevatedContainer.setInitialVisibility(timer, container);
        }
      } else {
        container.classList.toggle('hidden');
      }

      // Provide a public handling function for users to toggle the visibility on and off
      container.toggleVisibility = function(state = -1) {
        elevatedContainer.toggleVisibility(this, state !== -1 ? state : null);
      };
    });
  }),

  /**
   * This validates the content that it actually exists, so we don't show empty modals
   * @param  {object} container Container that has custom attributes.
   */
  validateContent: function(container) {
    let content = container.querySelector('.content');

    // if content div has no children, set forceHide to true
    if (content.children.length < 1) {
      elevatedContainer.forceHide = true;
    }
  },

  /**
   * Set dimensions to popup container
   * @param {string} position Element position
   * @param {string} positionTop Element position top
   * @param {string} positionRight Element position right
   * @param {string} positionBottom Element position bottom
   * @param {string} positionLeft Element position left
   * @param {string} width element width, only takes effect on mobile sizes
   * @param {object} container Popup-container
   */
  setPosition: function(position, positionTop, positionRight, positionBottom, positionLeft, width, container) {
    if (position) {
      container.style.position = position;
    }

    if (positionTop) {
      container.style.top = positionTop;
    }

    if (positionBottom) {
      container.style.bottom = positionBottom;
    }

    if (positionRight) {
      container.style.right = positionRight;
    }

    if (positionLeft) {
      container.style.left = positionLeft;
    }

    //Screen size is smaller than tablet landscape
    if (window.innerWidth <= 1024 && typeof width !== 'undefined') {
      container.style.left = '50%';
      container.style.transform = 'translate(-50%, 0)';
      container.style.width = width;
    }
  },

  /**
   * Close button can be any element with class .btn-close or data attribute 'data-close'.
   * If those elements are clicked elevated-container is closed.
   * @param {HtmlElement} container elevated container HTMLElement
   */
  initToggleElements: function(container) {
    const closeButtons = container.querySelectorAll('.btn-close,[data-close]');
    closeButtons.forEach(button => {
      button.addEventListener('click', elevatedContainer.onCloseButtonClicked);
    });

    if (container.classList.contains('elevated-overlay')) {
      // Close modal by clicking the dimmed area
      container.addEventListener('click', event => {
        if (event.target === event.currentTarget) {
          elevatedContainer.toggleVisibility(container, false);
        }
      });

      // Close modal by clicking the ESC key
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && container.classList.contains('visible')) {
          elevatedContainer.toggleVisibility(container, false);
        }
      });
    }
  },
  /**
   * Set dimensions to popup container
   * @param {int} timer Delay in ms, if any
   * @param {object} container Popup-container
   * This will be instant (0ms) if not set
   */
  setInitialVisibility: function(timer, container) {
    setTimeout(function() {
      container.classList.toggle('visible');
      document.body.classList.toggle('elevated-container-open');
    }, timer);
  },

  /**
   * Called when elevated container's close button is clicked
   * @param {event} event Click event
   */
  onCloseButtonClicked(event) {
    event.preventDefault();
    const container = event.target.closest('.elevated-container');
    elevatedContainer.toggleVisibility(container);
  },

  /**
   * Toggle elevated container's visibility
   * @param {HTMLElement} container Click event
   * @param {boolean} [force] A Boolean that, if included, turns the toggle into a one way-only operation.
   *                         If set to false, then token will only be removed, but not added. If set to true,
   *                         then token will only be added, but not removed.
   */
  toggleVisibility(container, force) {
    const isVisible = container.classList.contains('visible');

    // Save the last focused element only if the container is about to be opened
    if (!isVisible) {
      this.lastFocusedElement = document.activeElement;
    }

    if (typeof force === 'boolean') {
      container.classList.toggle('visible', force);
      document.body.classList.toggle('elevated-container-open', force);
      document.body.classList.toggle('overlay-open', force);
      document.body.classList.toggle('no-scroll', force);
    } else {
      container.classList.toggle('visible');
      document.body.classList.toggle('elevated-container-open', container.classList.contains('visible'));
      document.body.classList.toggle('overlay-open', container.classList.contains('visible'));
      document.body.classList.toggle('no-scroll', container.classList.contains('visible'));
    }

    // When the container is opened, focus the container and set up focus trap
    if (!isVisible) {
      const focusableElements = container.querySelectorAll(
        'a[href], button, textarea, input:not([type="hidden"]), select, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }

      document.addEventListener('keydown', this._handleTrapFocus.bind(this, container));
    // When the container is closed, return focus to the last focused element
    } else {
      this.lastFocusedElement.focus();
      document.removeEventListener('keydown', this._handleTrapFocus.bind(this, container));
    }
  },

  /**
   * Trap focus inside modal, so that it doesn't go outside of it when modal is open
   * @param {HTMLElement} container - Modal container
   * @param {KeyboardEvent} e - Keyboard event
   */
  _handleTrapFocus(container, e) {
    if (e.key !== 'Tab') {
      return;
    }

    const focusableElements = container.querySelectorAll(
      'a[href], button, textarea, input:not([type="hidden"]), select, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        lastElement.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastElement) {
        firstElement.focus();
        e.preventDefault();
      }
    }
  },

  /**
   * Method to get all custom attributes from a popup container.
   * @param  {object} container Container that has custom attributes.
   * @return {array} customAttributes Array of custom attributes.
   */
  getCustomAttrs: function(container) {
    let customContainer = container.querySelector('.content');
    let customAttributes = [];

    if (customContainer) {
      // get all attributes that start with data-
      // customContainer.attributes is not an array (It's HTMLCollection), so we'll create a prototype array and call a filter method
      let dataAttrs = [].filter.call(customContainer.attributes, data => /^data-/.test(data.name));

      // normalized array for all data-attributes
      dataAttrs.forEach(attribute => {
        let elemName = attribute.nodeName;
        let elemValue = attribute.nodeValue;
        customAttributes.push({ name: elemName, value: elemValue });
      });
    }

    return customAttributes;
  },
};

export default elevatedContainer;
